import * as FileSaver from 'file-saver';
import api from 'services/api';
import * as types from './types';
import get from 'lodash/get';
import isEmpty from 'lodash/isEmpty';
import set from 'lodash/set';
import { enqueueCommand } from 'Common/Components/FileUploadContext/actions';
import {
  buildGroupingCommandFromResponse,
  extractCommandIdFromResponse,
  extractIsGroupingFromResponse,
  extractUpdatedVulnerabilityFromResponse,
  getVulnerabilityNameErrorMessage,
  normalizeVulnerabilityName
} from 'Common/Functions/vulnerabilityNameEdit';
import {
  shouldTreatCreatedVulnAsGrouping,
  shouldTreatFieldUpdateAsGrouping
} from 'Common/Functions/vulnerabilityGrouping';
import { logGroupingDebug } from 'Common/Functions/groupingCommandDebug';
import applyOptimisticStatusHistory from 'Common/Functions/vulnerabilityStatusHistory';
import { selectWorkspaces, selectUserName } from 'store/Faraday/selectors';
import {
  selectBulkUpdateCustomAttribute, selectContextMenuXPos, selectContextMenuYPos, selectLastSelected,
  selectModalBulkUpdateField, selectModalBulkUpdateValue, selectSelectAll,
  selectShowDetail, selectVulnDetail, selectVulnDetailId, selectVulnWorkspace,
  selectVulns, selectVulnsCount, selectVulnsSelected
} from './selectors';
import { selectAdvancedExpandedQueryParam, selectAdvancedFilterQueryParam, selectExpandedQueryParam, selectGroupByField, selectIsGrouping, selectQueryParam, selectRowsPerPage } from 'store/Filters/selectors';
import { addFilter, clearFilters, clearGroupBy, expandGroupBy, setFilter, setFilterError, setGroupBy, setOrderBy, setPageNumber } from 'store/Filters/actions';
import { MODAL_MANAGE_BULK_UPDATE_CONFIRMATION, MODAL_MANAGE_BULK_UPDATE } from 'store/modals/modals';
import { getImpactData } from 'store/Manage/actions';
import { closeModal, openModal } from 'store/modals/actions';
import { redirect } from 'store/Router/actions';
import { contextMenufilteringFunctions } from 'store/Manage/filterActions';
import { getVulnsCountInAllWorkspaces } from 'Screens/Contextualization/Faraday/actions/Actions';
import { MANAGE_CREATE_UPDATE_FAIL, MANAGE_CREATE_UPDATE_START, RESET_STATE_MANAGE_CREATE_UPDATE } from 'Screens/Contextualization/ManageEditCreate/actions/Actions';
import { getGeneralHostsTags, getGeneralServicesTags, getGeneralTags } from 'Screens/Contextualization/Tags/actions/Actions';
import {
  EXPORT_CSV_MANAGE_FAILURE,
  EXPORT_CSV_MANAGE_REQUEST,
  EXPORT_CSV_MANAGE_SUCCESS,
  GET_USERS_SUCCESS,
  MANAGE_SET_TEMP_CVSS
} from 'store/Manage/types';
import { selectVulnsGeneralFields } from 'store/Preferences/vulnsGeneral/selectors';
import VALID_COLUMNS from 'Screens/Contextualization/Manage/components/CustomActionBar/components/ExportCSV/constants';
import { hasUnsupportedVisibleVulnColumns, withVulnColumns } from 'store/Manage/vulnColumns';
import { buildEmptyExportCsv, buildExportCsv, getExportCsvColumns } from 'store/Manage/exportCsv';
import { getVulnId, normalizeVulnIds, sameVulnId } from 'Common/Functions/vulnerabilityId';

const getSingleNameUpdateTarget = (state) => {
  const vulnsSelected = selectVulnsSelected(state);
  if (vulnsSelected.length === 1) return vulnsSelected[0];

  const vulnDetail = selectVulnDetail(state);
  return isEmpty(vulnDetail) ? null : vulnDetail;
};

export const resetState = () => (dispatch) => dispatch({ type: types.GENERAL_MANAGE_RESET_STATE });

export const somethingWentWrong = (errorMessage) => async (dispatch) => dispatch({ type: types.GENERAL_MANAGE_FAIL, errorMessage: errorMessage || 'There was an error, please try again.' });

export const clearError = () => async (dispatch) => dispatch({ type: types.GENERAL_MANAGE_CLEAR_ERROR });

export const setVulnsFilterError = () => async (dispatch) => dispatch({ type: types.GENERAL_MANAGE_SET_VULNS_FILTER_ERROR });

const getVulnsGeneralQueryParam = (queryParam, fields) => {
  // VulnsGeneral keeps requesting the full payload while it has visible
  // columns that backend cannot serve via `columns` (for example any visible
  // custom field).
  return withVulnColumns(queryParam, fields, {
    keepFullPayload: hasUnsupportedVisibleVulnColumns(fields)
  });
};

const normalizeVulnValue = (vuln) => normalizeVulnIds(get(vuln, 'value', {}));

const exportCsv = (result) => async () => {
  const fileType = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8';
  const fileExtension = '.csv';
  const data = new Blob([result], { type: fileType });
  FileSaver.saveAs(data, `Vulnerability Model CSV${fileExtension}`);
};

const withEmptyFilters = (queryParam = {}) => {
  if (Array.isArray(queryParam.filters)) return queryParam;
  return { ...queryParam, filters: [] };
};

const withoutColumns = (queryParam = {}) => {
  const fallbackQueryParam = { ...queryParam };
  delete fallbackQueryParam.columns;
  return fallbackQueryParam;
};

const exportCsvFromJson = async (queryParam, columnsIncluded) => {
  const response = await api.vulnsGeneral.fetchVulns(withoutColumns(queryParam));
  const vulns = get(response, 'vulnerabilities', []).map((vuln) => vuln.value);
  return buildExportCsv(columnsIncluded, vulns);
};

export const getVulnsGeneral = () => async (dispatch, getState) => {
  dispatch({ type: types.GENERAL_MANAGE_GET_REQUEST });
  let advancedFilterQueryParam = [];

  try {
    advancedFilterQueryParam = selectAdvancedFilterQueryParam(getState(), 'vulnsGeneral');
  } catch (e) {
    dispatch(setFilterError('vulns', 'Syntax error. Please try again. For further help check our documentation'));
  }

  const hasAdvancedFilter = advancedFilterQueryParam.filters.length > 0;
  const standardQueryParam = selectQueryParam('vulnsGeneral', getState());
  const fields = selectVulnsGeneralFields(getState());
  const queryParam = getVulnsGeneralQueryParam(hasAdvancedFilter ? advancedFilterQueryParam : standardQueryParam, fields);

  const groupBy = selectIsGrouping('vulnsGeneral', getState());

  try {
    const response = await api.vulnsGeneral.fetchVulns(queryParam);
    if (!response.status || response.status !== 'canceled') {
      const vulns = get(response, 'vulnerabilities', []);
      const result = vulns.map(normalizeVulnValue);
      const vulnsCountArray = vulns.map((vuln) => vuln.value.count);
      const vulnsCountGroupBy = vulnsCountArray.reduce((prev, cur) => prev + cur, 0);
      return dispatch({
        type: types.GENERAL_MANAGE_GET_SUCCESS,
        data: result,
        count: groupBy ? vulnsCountGroupBy : response.count
      });
    }
  } catch (error) {
    const errorMessage = get(error, 'message', 'Failed to obtain vulnerabilities.');
    if (errorMessage.includes('filter')) {
      dispatch(setFilterError('vulnsGeneral', 'Invalid filters. Please try again. For further help check our documentation'));
      return dispatch(setVulnsFilterError());
    }
    return dispatch(somethingWentWrong(errorMessage));
  }
};

export const downloadVulnsGeneral = () => async (dispatch, getState) => {
  const fields = selectVulnsGeneralFields(getState());
  const columnsIncluded = getExportCsvColumns(fields, VALID_COLUMNS);
  const state = getState();
  const vulnsCount = selectVulnsCount(state);
  let advancedFilterQueryParam = [];

  try {
    advancedFilterQueryParam = selectAdvancedFilterQueryParam(state, 'vulnsGeneral', true, columnsIncluded);
  } catch (e) {
    dispatch(setFilterError('vulnsGeneral', 'Syntax error. Please try again. For further help check our documentation'));
    return dispatch(setVulnsFilterError());
  }

  const hasAdvancedFilter = advancedFilterQueryParam.filters.length > 0;
  const standardQueryParam = selectQueryParam('vulnsGeneral', state, true, columnsIncluded);
  const queryParam = withEmptyFilters(hasAdvancedFilter ? advancedFilterQueryParam : standardQueryParam);

  try {
    dispatch({ type: EXPORT_CSV_MANAGE_REQUEST });
    let response = '';
    if (vulnsCount === 0) {
      response = buildEmptyExportCsv(columnsIncluded);
    } else {
      try {
        response = await api.vulnsGeneral.exportCSVFiltered(queryParam);
      } catch (err) {
        response = await exportCsvFromJson(queryParam, columnsIncluded);
      }
    }
    dispatch(exportCsv(response));
  } catch (err) {
    dispatch({ type: EXPORT_CSV_MANAGE_FAILURE });
  }

  return dispatch({ type: EXPORT_CSV_MANAGE_SUCCESS });
};

const addDeleteController = (vulns, vulnList) => {
  const allVulnsAreSelected = vulns.every((testVuln) => vulnList.some((vuln) => sameVulnId(vuln, testVuln)));

  if (allVulnsAreSelected) return [types.GENERAL_MANAGE_UNSELECTED, vulns];
  return [types.GENERAL_MANAGE_SELECTED, vulns];
};

const selectCalculator = (e, vuln, vulnList, areVulnsSelected) => (dispatch, getState) => {
  const pivot = selectLastSelected(getState());
  const index = vulnList.findIndex((el) => sameVulnId(el, vuln));
  const vulnsSelected = selectVulnsSelected(getState());

  dispatch({ type: types.GENERAL_MANAGE_NEW_PIVOT, payload: index });
  if (e.shiftKey && pivot !== -1 && areVulnsSelected) {
    const start = Math.min(pivot, index);
    const end = Math.max(pivot, index) + 1;
    const vulns = vulnList.slice(start, end);
    const [type, payload] = addDeleteController(vulns, vulnsSelected);
    return dispatch({ type, payload });
  }
  const [type, payload] = addDeleteController([vuln], vulnsSelected);
  return dispatch({ type, payload });
};

export const selectVulnRow = (e, vuln) => (dispatch, getState) => {
  const allVulns = selectVulns(getState());
  const isGroupingBy = selectIsGrouping('vulnsGeneral', getState());
  const vulnsSelected = selectVulnsSelected(getState());

  if (isGroupingBy) {
    const group = allVulns.find((g) => g.groupData && g.groupData.find((el) => sameVulnId(el, vuln)));
    if (!group) return;
    const vulnGroup = group.groupData;
    const areVulnsSelected = vulnsSelected.length > 0 && vulnGroup.some((el) => sameVulnId(el, vuln));
    dispatch(selectCalculator(e, vuln, vulnGroup, areVulnsSelected, vulnsSelected));
  } else {
    dispatch(selectCalculator(e, vuln, allVulns, vulnsSelected.length > 0));
  }
};

export const unSelectAllVulns = () => (dispatch) => dispatch({ type: types.GENERAL_MANAGE_UNSELECT_ALL });

export const selectAllVulns = () => (dispatch, getState) => {
  const vulnsList = selectVulns(getState());
  const vulnsSelected = selectVulnsSelected(getState());
  const vulnCount = selectVulnsCount(getState());
  const pageSize = selectRowsPerPage('vulnsGeneral', getState());

  if (vulnsSelected.length === vulnCount || vulnsSelected.length >= pageSize) return dispatch(unSelectAllVulns());

  return dispatch({ type: types.GENERAL_MANAGE_SELECT_ALL, vulnsList });
};

export const selectTotalVulns = () => (dispatch) => {
  return dispatch({ type: types.GENERAL_MANAGE_SELECT_TOTAL_VULNS });
};

export const unselectTotalVulns = () => (dispatch) => {
  return dispatch({ type: types.GENERAL_MANAGE_UNSELECT_TOTAL_VULNS });
};

export const setShowContextMenu = (show, XPos, YPos) => (dispatch, getState) => {
  const state = getState();
  const currentXPos = selectContextMenuXPos(state);
  const currentYPos = selectContextMenuYPos(state);
  const newXPos = XPos ? (XPos + 1) : currentXPos;
  const newYPos = YPos ? (YPos + 1) : currentYPos;

  dispatch({
    type: types.GENERAL_MANAGE_SHOW_CONTEXT_MENU, show, contextMenuXPos: newXPos, contextMenuYPos: newYPos
  });
};

export const setContextMenuFilter = (filterKey, selectedVuln) => (dispatch) => {
  const newFilter = contextMenufilteringFunctions[filterKey](selectedVuln);
  if (filterKey === 'tags') {
    dispatch(clearFilters('vulnsGeneral'));
    newFilter.forEach((tagFilter) => {
      dispatch(addFilter('vulnsGeneral', tagFilter));
    });
  } else if (newFilter) dispatch(setFilter('vulnsGeneral', newFilter));
  dispatch(getVulnsGeneral());
};

export const onGetVulnsFail = (errorMessage) => (dispatch) => dispatch({ type: types.GENERAL_MANAGE_SEARCH_VULNS_FAIL, data: errorMessage });

const getExpandedGroupByData = (index) => async (dispatch, getState) => {
  dispatch({ type: types.GENERAL_MANAGE_GET_GROUPED_DATA_START });
  const state = getState();

  let advancedFilterQueryParam = [];
  try {
    advancedFilterQueryParam = selectAdvancedExpandedQueryParam(state, 'vulnsGeneral');
  } catch (e) {
    dispatch(setFilterError('vulns', 'Syntax error. Please try again. For further help check our documentation'));
  }
  const hasAdvancedFilter = advancedFilterQueryParam.filters.length > 0;
  const standardQueryParam = selectExpandedQueryParam('vulnsGeneral', state);
  const fields = selectVulnsGeneralFields(state);
  const queryParam = getVulnsGeneralQueryParam(hasAdvancedFilter ? advancedFilterQueryParam : standardQueryParam, fields);

  try {
    const response = await api.vulnsGeneral.fetchVulns(queryParam);

    if (!response.status || response.status !== 'canceled') {
      const result = response.vulnerabilities.map(normalizeVulnValue);

      return dispatch({
        type: types.GENERAL_MANAGE_GET_GROUPED_DATA_SUCCESS,
        data: result,
        index
      });
    }
  } catch (e) {
    const errorMessage = get(e, 'message', 'Failed to obtain vulnerabilities.');
    if (errorMessage.includes('filter')) {
      dispatch(setFilterError('vulns', 'Invalid filters. Please try again. For further help check our documentation'));
    }
    return dispatch(somethingWentWrong(errorMessage));
  }
};

export const setGroupByVulnsGeneral = (group_by) => async (dispatch) => {
  const field = get(group_by, '[0].field', '');
  await dispatch(setGroupBy('vulnsGeneral', group_by));
  if (field) await dispatch(setOrderBy('vulnsGeneral', [{ id: field, desc: field === 'severity' }]));
  dispatch(getVulnsGeneral());
};

export const clearGroupByVulnsGeneral = () => async (dispatch) => await dispatch(clearGroupBy('vulnsGeneral'));

export const expandGroupByVulns = (index, data) => async (dispatch) => {
  await dispatch(expandGroupBy('vulnsGeneral', data));
  dispatch(getExpandedGroupByData(index));
};

export const setOrderByVulnsGeneral = (sorting) => (dispatch, getState) => {
  const state = getState();
  const isGrouping = selectIsGrouping('vulnsGeneral', state);

  dispatch(setOrderBy('vulnsGeneral', sorting));
  if (!isGrouping) dispatch(getVulnsGeneral());
};

export const setBulkUpdateField = (field) => (dispatch) => {
  return dispatch({ type: types.GENERAL_MANAGE_SET_BULK_UPDATE_FIELD, field });
};

export const setBulkUpdateValue = (value) => (dispatch) => {
  return dispatch({ type: types.GENERAL_MANAGE_SET_BULK_UPDATE_VALUE, value });
};

export const addBulkUpdateValue = (value) => (dispatch) => {
  return dispatch({ type: types.GENERAL_MANAGE_ADD_BULK_UPDATE_VALUE, value });
};

export const removeBulkUpdateValue = (value) => (dispatch) => {
  return dispatch({ type: types.GENERAL_MANAGE_REMOVE_BULK_UPDATE_VALUE, value });
};

export const refreshVulnsList = (vulnsList, vulnsSelected, vulnDetail) => (dispatch, getState) => {
  dispatch({
    type: types.GENERAL_MANAGE_REFRESH_VULNS, vulnsList, vulnsSelected, vulnDetail, vulnsCount: selectVulnsCount(getState())
  });
};

export const setCustomAttributeField = (customAttribute) => (dispatch) => {
  dispatch({ type: types.GENERAL_MANAGE_SET_BULK_CUSTOM_ATTRIBUTE, customAttribute });
};

export const bulkUpdateVulns = () => async (dispatch, getState) => {
  const state = getState();
  const field = selectModalBulkUpdateField(state);
  const value = selectModalBulkUpdateValue(state);
  const singleNameTarget = field === 'name' ? getSingleNameUpdateTarget(state) : null;
  const normalizedNameValue = field === 'name' ? normalizeVulnerabilityName(value) : value;

  if (field === 'name' && singleNameTarget && normalizeVulnerabilityName(singleNameTarget.name) === normalizedNameValue) {
    dispatch(closeModal(MODAL_MANAGE_BULK_UPDATE));
    dispatch(closeModal(MODAL_MANAGE_BULK_UPDATE_CONFIRMATION));
    dispatch({ type: types.RESET_ERROR_VALUE });
    dispatch({ type: types.GENERAL_MANAGE_BULK_UPDATE_FINISHED });
    return;
  }

  dispatch({ type: types.GENERAL_MANAGE_CONFIRMATION_CHANGE_START });

  try {
    const vulnsList = selectVulns(state);
    const vulnsSelected = selectVulnsSelected(state);
    const vulnDetail = selectVulnDetail(state);
    const selectAll = selectSelectAll(state);
    const vulnIDs = vulnsSelected.map(getVulnId).filter((id) => id !== null && typeof id !== 'undefined');
    const customAttribute = selectBulkUpdateCustomAttribute(state);
    const customAttributeType = get(customAttribute, 'field_type', '');
    const customAttributeFieldName = get(customAttribute, 'field_name', '');
    const customAttributeValue = ((customAttributeType === 'choice') && (value === '(Empty)')) ? '' : value;

    const advancedFilterQueryParam = selectAdvancedFilterQueryParam(state, 'vulnsGeneral');
    const hasAdvancedFilter = advancedFilterQueryParam.filters.length > 0;
    const standardQueryParam = selectQueryParam('vulnsGeneral', state);
    const queryParam = hasAdvancedFilter ? advancedFilterQueryParam : standardQueryParam;

    let data = {};

    if (field === 'references') data = { refs: value.map((v) => ({ name: v, type: 'other' })) };
    else if (field === 'policy violations') data = { policyviolations: value };
    else if (field === 'description') data = { description: value, desc: value };
    else if (field === 'impact') data = getImpactData(value, vulnsSelected);
    else if (field === 'custom_fields' && (vulnsSelected?.length > 1)) data = { custom_fields: { [customAttributeFieldName]: customAttributeValue } };
    else if (field === 'custom_fields' && (vulnsSelected?.length === 1)) data = { custom_fields: { ...vulnsSelected[0].custom_fields, [customAttributeFieldName]: customAttributeValue } };
    else data = { [field]: field === 'name' ? normalizedNameValue : value };

    const isDetailVulnSelected = vulnsSelected.some((selectedVuln) => !isEmpty(vulnDetail) && sameVulnId(selectedVuln, vulnDetail));

    if (selectAll) {
      await api.vulnsGeneral.updateAllVulns({ filters: queryParam.filters }, data);

      const currentUser = selectUserName(state);
      const patchStatus = (vuln) => field === 'status'
        ? applyOptimisticStatusHistory({ ...vuln, ...data }, value, currentUser)
        : { ...vuln, ...data };
      const updatedVulnsList = vulnsList.map(patchStatus);
      const updatedSelectedVulns = vulnsSelected.map(patchStatus);
      const updatedVulnDetail = isDetailVulnSelected ? patchStatus(vulnDetail) : { ...vulnDetail };

      dispatch(refreshVulnsList(updatedVulnsList, updatedSelectedVulns, updatedVulnDetail));
    } else if (field === 'name' && singleNameTarget) {
      const workspaceName = get(singleNameTarget, 'workspace_name', '');
      const groupingStartTimestamp = Date.now();
      const response = await api.manage.updateVuln(workspaceName, {
        _id: singleNameTarget._id,
        name: normalizedNameValue
      });

      const updatedVuln = extractUpdatedVulnerabilityFromResponse(response, {
        ...singleNameTarget,
        name: normalizedNameValue
      });
      const commandId = extractCommandIdFromResponse(response);
      const isGroupingFromResponse = extractIsGroupingFromResponse(response);
      const workspaces = selectWorkspaces(state);
      const shouldEnqueueGrouping = shouldTreatFieldUpdateAsGrouping({
        workspaceName,
        workspaces,
        field: 'name',
        commandId,
        isGrouping: isGroupingFromResponse
      });
      let groupingDecisionSource = 'response.missing';
      if (isGroupingFromResponse === true) groupingDecisionSource = 'response.is_grouping';
      else if (shouldEnqueueGrouping) groupingDecisionSource = 'workspace.group_by_fallback';
      logGroupingDebug('name_edit_patch_response', {
        source: 'manageGeneral.bulkUpdate.singleName',
        workspaceName,
        vulnerabilityId: singleNameTarget?._id,
        commandId,
        isGroupingFromResponse,
        shouldEnqueueGrouping,
        groupingDecisionSource
      });

      dispatch(vulnPreviewEditSuccedCallback(singleNameTarget, updatedVuln));
      if (shouldEnqueueGrouping) {
        logGroupingDebug('name_edit_grouping_enqueued', {
          source: 'manageGeneral.bulkUpdate.singleName',
          workspaceName,
          commandId
        });
        dispatch(enqueueCommand({
          command_id: commandId ?? null,
          commandType: 'grouping',
          name: workspaceName,
          fileType: 'grouping',
          isGrouping: true,
          groupingStartTimestamp,
          error: false,
          finished: false
        }));
        dispatch(redirectToVulnerabilities());
      }

      dispatch({ type: types.GENERAL_MANAGE_SET_BULK_UPDATE_SUCCESS });
    } else {
      const payload = { ids: vulnIDs, ...data };

      await api.vulnsGeneral.updateVulns(payload);

      const currentUser = selectUserName(state);
      const patchStatus = (vuln) => field === 'status'
        ? applyOptimisticStatusHistory({ ...vuln, ...data }, value, currentUser)
        : { ...vuln, ...data };
      const updatedVulnsList = vulnsList.map((vuln) => {
        if (!vulnsSelected.some((selectedVuln) => sameVulnId(selectedVuln, vuln))) return vuln;
        return patchStatus(vuln);
      });
      const updatedSelectedVulns = vulnsSelected.map(patchStatus);
      const updatedVulnDetail = isDetailVulnSelected ? patchStatus(vulnDetail) : { ...vulnDetail };

      dispatch({ type: types.GENERAL_MANAGE_SET_BULK_UPDATE_SUCCESS });
      dispatch(refreshVulnsList(updatedVulnsList, updatedSelectedVulns, updatedVulnDetail));
    }

    dispatch(closeModal(MODAL_MANAGE_BULK_UPDATE));
    dispatch(closeModal(MODAL_MANAGE_BULK_UPDATE_CONFIRMATION));
    dispatch({ type: types.GENERAL_MANAGE_BULK_UPDATE_FINISHED });
  } catch (e) {
    dispatch(closeModal(MODAL_MANAGE_BULK_UPDATE_CONFIRMATION));
    if (field === 'name') dispatch({ type: types.SET_ERROR_MESSAGE, errorMessage: getVulnerabilityNameErrorMessage(e) });
    else {
      dispatch({ type: types.GENERAL_MANAGE_FAIL, errorMessage: e.message || 'An error occured while updating vulns' });
      dispatch(closeModal(MODAL_MANAGE_BULK_UPDATE));
    }
  }
};

export function showBulkUpdateModal (bulkUpdateField, bulkUpdateValue) {
  return (dispatch) => {
    dispatch(setBulkUpdateField(bulkUpdateField));
    dispatch(setBulkUpdateValue(bulkUpdateValue));
    dispatch(openModal(MODAL_MANAGE_BULK_UPDATE));
  };
}

function vulnPreviewEditSuccedCallback (vulnBefore, vulnAfter) {
  return (dispatch, getState) => {
    const groupBy = selectGroupByField('vulnsGeneral', getState());
    const vulnsList = selectVulns(getState());

    if (groupBy) {
      // se actualiza la tabla agrupada
      const indexGroupDataAfter = vulnsList.findIndex((x) => x[groupBy] === vulnAfter[groupBy]);
      const groupDataAfter = vulnsList[indexGroupDataAfter].groupData || [];
      const indexVulnAfter = groupDataAfter.findIndex((x) => sameVulnId(x, vulnAfter));
      if (indexVulnAfter < 0) { // no lo encontro, lo borro
        const indexGroupDataBefore = vulnsList.findIndex((x) => x[groupBy] === vulnBefore[groupBy]);
        const groupDataBefore = vulnsList[indexGroupDataBefore].groupData || {};
        const indexVulnBefore = groupDataBefore.findIndex((x) => sameVulnId(x, vulnAfter));
        // lo quito del grupo.
        vulnsList[indexGroupDataBefore].groupData.splice(indexVulnBefore, 1);
        // eslint-disable-next-line no-negated-condition
        if (!vulnsList[indexGroupDataAfter].groupData) vulnsList[indexGroupDataAfter].count += 1;
        else vulnsList[indexGroupDataAfter].groupData.push(vulnAfter);
      } else {
        vulnsList[indexGroupDataAfter].groupData[indexVulnAfter] = vulnAfter;
      }
    } else {
      // se actualiza la tabla
      const index = vulnsList.findIndex((x) => sameVulnId(x, vulnAfter));
      vulnsList[index] = vulnAfter;
    }

    dispatch({ type: types.GENERAL_MANAGE_UPDATE_PREVIEW_SUCCESS, vuln: vulnAfter, vulnsList });
  };
}

function vulnPreviewEditErrorCallback (error) {
  return (dispatch) => {
    dispatch({ type: types.GENERAL_MANAGE_UPDATE_PREVIEW_FAILURE, error: error.message || 'An error has occurred' });
  };
}

export const updateVulnGeneral = (vulnBefore, field, value, options = {}) => async (dispatch, getState) => {
  const workspace = get(vulnBefore, 'workspace_name', '');
  const groupingStartTimestamp = Date.now();
  let vulnAfter = { ...vulnBefore };
  set(vulnAfter, field, value);

  try {
    let newRefs = [];
    let rawResponse = null;
    if (field === 'refs') {
      newRefs = value.map((ref) => ({ name: ref, type: 'other' }));
      rawResponse = await api.manage.updateVuln(workspace, { _id: vulnBefore._id, refs: newRefs });
    } else {
      rawResponse = await api.manage.updateVuln(workspace, { _id: vulnBefore._id, [field]: value });
    }
    vulnAfter = extractUpdatedVulnerabilityFromResponse(rawResponse, vulnAfter);

    if (field === 'status') {
      vulnAfter = applyOptimisticStatusHistory(vulnAfter, value, selectUserName(getState()), vulnBefore.status_history);
    }

    const commandId = extractCommandIdFromResponse(rawResponse);
    const isGrouping = extractIsGroupingFromResponse(rawResponse) === true;
    const groupingCommand = options.enqueueGroupingFromResponse === false
      ? null
      : buildGroupingCommandFromResponse(rawResponse, {
        workspaceName: workspace,
        groupingStartTimestamp,
        commandId
      });
    if (field === 'name') {
      logGroupingDebug('name_edit_patch_response', {
        source: 'manageGeneral.updateVulnGeneral',
        workspaceName: workspace,
        vulnerabilityId: vulnBefore?._id,
        commandId
      });
    }

    dispatch(vulnPreviewEditSuccedCallback(vulnBefore, vulnAfter));
    if (groupingCommand) dispatch(enqueueCommand(groupingCommand));
    return {
      ok: true,
      updatedVuln: vulnAfter,
      commandId,
      isGrouping,
      response: rawResponse,
      groupingEnqueued: Boolean(groupingCommand)
    };
  } catch (error) {
    dispatch(vulnPreviewEditErrorCallback(error));
    return {
      ok: false,
      error
    };
  }
};

export const redirectToVulnerabilities = () => (dispatch) => {
  return dispatch(redirect('/vulnerabilities'));
};

export const redirectToVulnDetailGeneral = (id) => (dispatch) => {
  return dispatch(redirect(`/vulnerabilities/${id}`));
};

export const setVulnerabilityDetailTab = (vulnDetailSelectedTab) => (dispatch) => {
  return dispatch({ type: types.GENERAL_MANAGE_SET_VULN_DETAIL_TAB, vulnDetailSelectedTab });
};

export const showVulnDetailGeneral = (id, vulnDetailSelectedTab) => (dispatch) => {
  if (id > 0) {
    dispatch(redirect(`/vulnerabilities/${id}`));
    dispatch(setVulnerabilityDetailTab(vulnDetailSelectedTab));
  }
};

export const loadVulnDetail = (id) => async (dispatch) => {
  if (id > 0) {
    dispatch({ type: types.GENERAL_MANAGE_LOAD_VULN_DETAIL_REQUEST });
    try {
      const vuln = await api.vulnsGeneral.fetchVulnById(id);
      dispatch({ type: types.GENERAL_MANAGE_SHOW_VULN_DETAIL, vuln });
    } catch (e) {
      dispatch(redirectToVulnerabilities());
    }
  }
};

export const hideVulnDetail = () => (dispatch, getState) => {
  const showVulnDetail = selectShowDetail(getState());
  if (showVulnDetail) dispatch({ type: types.GENERAL_MANAGE_HIDE_VULN_DETAIL });
};

export const setPage = (pageNumber) => {
  return (dispatch) => {
    dispatch(setPageNumber('vulnsGeneral', pageNumber));
    dispatch(getVulnsGeneral());
  };
};

export function showVulnModalDelete () {
  return (dispatch) => {
    dispatch({ type: types.GENERAL_MANAGE_SHOW_MODAL_DELETE_CONFIRMATION });
  };
}

// Summary: Hide confirmation modal when user delete vuln/s
export function hideVulnModalDelete () {
  return (dispatch) => {
    dispatch({ type: types.GENERAL_MANAGE_HIDE_MODAL_DELETE_CONFIRMATION });
  };
}

export const deleteSelectedVulns = () => async (dispatch, getState) => {
  const vulnsSelected = selectVulnsSelected(getState());
  const vulnIDs = vulnsSelected.map(getVulnId).filter((id) => id !== null && typeof id !== 'undefined');
  const groupByField = selectGroupByField('vulnsGeneral', getState());
  const vulnsList = selectVulns(getState());
  const selectAll = selectSelectAll(getState());

  const advancedFilterQueryParam = selectAdvancedFilterQueryParam(getState(), 'vulnsGeneral');
  const hasAdvancedFilter = advancedFilterQueryParam.filters.length > 0;
  const standardQueryParam = selectQueryParam('vulnsGeneral', getState());
  const queryParam = hasAdvancedFilter ? advancedFilterQueryParam : standardQueryParam;

  dispatch({ type: types.GENERAL_MANAGE_HIDE_MODAL_DELETE_CONFIRMATION_SUCCEED });
  dispatch({ type: types.GENERAL_MANAGE_DELETE });

  try {
    if (selectAll) {
      await api.vulnsGeneral.deleteAllVulns({ filters: queryParam.filters });
    } else {
      await api.vulnsGeneral.deleteVulns(vulnIDs);
    }

    if (groupByField) {
      const newVulnsList = vulnsList.map((group) => {
        const groupedData = get(group, 'groupData', null);
        if (groupedData) {
          const newGroupedData = groupedData.filter((vuln) => !vulnIDs.some((id) => sameVulnId(vuln, { id })));
          return {
            ...group,
            count: newGroupedData.length,
            groupData: newGroupedData
          };
        }
        return group;
      });
      dispatch({ type: types.GENERAL_MANAGE_UPDATE_VULNS_LIST, vulnsList: newVulnsList });
    } else dispatch(getVulnsGeneral());
  } catch (e) {
    dispatch({ type: types.GENERAL_MANAGE_FAIL, errorMessage: e.message || 'An error occured while deleting vulns' });
  }
};

export const showVulnsGeneralRightFilters = (visible) => (dispatch) => dispatch({ type: types.GENERAL_MANAGE_SHOW_FILTERS, visible });

export function addEvidences (evidences, getEvidences) {
  return async (dispatch, getState) => {
    const responseSession = await api.faraday.getSession();
    const id = selectVulnDetailId(getState());
    const workspace = selectVulnWorkspace(getState());
    const promises = evidences.map((evidence) => {
      const newEvidenceName = evidence.name.replace(/\s+/g, '');
      const renameEvidence = new File([evidence], newEvidenceName);
      const fd = new FormData();
      fd.append('csrf_token', responseSession.csrf_token);
      fd.append('file', renameEvidence);
      fd.append('description', '');
      return api.attachments.saveAttachments(workspace, id, fd);
    });

    try {
      await Promise.all(promises);
    } catch (e) {
      const doc = e.message ? new DOMParser().parseFromString(e.message, 'text/html') : null;
      const message = e.message ? doc.documentElement.textContent : 'An error has occurred';
      dispatch({ type: types.ADD_EVIDENCE_FAILURE, error: message });
    }
    getEvidences();
  };
}

export const getVulnRefs = (id) => async (dispatch) => {
  try {
    const response = await api.vulnsGeneral.fetchVulnById(id);
    const refs = get(response, 'refs', []);
    dispatch(showBulkUpdateModal('references', refs.map((r) => r.name)));
  } catch (e) {
    dispatch({ type: types.GENERAL_MANAGE_FAIL, errorMessage: e.message || 'An error has occurred' });
  }
};

export function createVulnOutsideWs () {
  return async (dispatch, getState) => {
    dispatch({ type: MANAGE_CREATE_UPDATE_START });

    const state = getState().manageEditCreate;
    const workspaces = selectWorkspaces(getState());
    const groupingStartTimestamp = Date.now();
    const newRefs = state.references.map((ref) => ({ name: ref, type: 'other' }));
    const isWebVuln = state.isWebVuln;
    const servicesTargets = state.targets.filter((target) => target.value.type === 'Service');
    const servicesParents = servicesTargets.map((service) => service.value.parent);
    const allTargets = state.targets;
    const hostsTargets = allTargets.filter((target) => target.value.type === 'Host');
    const removeServiceParent = hostsTargets.filter((target) => !servicesParents.includes(target.value.id));
    const filteredTargets = servicesTargets.length > 0 ? servicesTargets.concat(removeServiceParent) : allTargets;
    const vulnsTargets = isWebVuln ? servicesTargets : filteredTargets;
    const hasCvss2 = state?.cvss2;
    const hasCvss3 = state?.cvss3;
    const hasCvss4 = state?.cvss4;

    try {
      const promises = vulnsTargets.map((target) => {
        let vuln = {
          _id: state.id,
          confirmed: state.confirmed,
          custom_fields: state.customFields,
          data: state.data,
          desc: state.description,
          description: state.description,
          easeofresolution: state.easeOfResolution,
          external_id: state.externalId,
          impact: {
            accountability: get(state, 'accountability', false),
            availability: get(state, 'availability', false),
            confidentiality: get(state, 'confidentiality', false),
            integrity: get(state, 'integrity', false)
          },
          method: state.method,
          name: state.name,
          pname: state.paramName,
          params: state.params,
          parent: target.value.id,
          parent_type: target.value.type,
          path: state.path,
          policyviolations: state.policies,
          query: state.query,
          refs: newRefs,
          request: state.request,
          resolution: state.resolution,
          response: state.response,
          severity: state.severity,
          status_code: state.status_code ? state.status_code : 0,
          type: state.isWebVuln ? 'VulnerabilityWeb' : 'Vulnerability',
          website: state.website,
          _attachments: state._attachments, // eslint-disable-line no-underscore-dangle
          cve: state.CVE,
          cwe: state.CWE
        };
        if (hasCvss2) vuln = { ...vuln, cvss2: state?.cvss2 };
        if (hasCvss3) vuln = { ...vuln, cvss3: state?.cvss3 };
        if (hasCvss4) vuln = { ...vuln, cvss4: state?.cvss4 };

        return api.manage.createVuln(target.value.workspace_name, vuln);
      });
      const res = await Promise.all(promises);
      const groupingCommands = (res || []).map((response, index) => {
        const workspaceName = vulnsTargets[index]?.value?.workspace_name;
        const commandId = extractCommandIdFromResponse(response);
        const isGrouping = shouldTreatCreatedVulnAsGrouping({
          workspaceName,
          workspaces,
          commandId,
          isGrouping: extractIsGroupingFromResponse(response) === true,
          name: state.name,
          cve: state.CVE
        });

        return isGrouping ? { commandId, workspaceName, groupingStartTimestamp } : null;
      }).filter(Boolean);

      groupingCommands.forEach(({ commandId, workspaceName, groupingStartTimestamp }) => {
        dispatch(enqueueCommand({
          command_id: commandId ?? null,
          commandType: 'grouping',
          name: workspaceName,
          fileType: 'grouping',
          isGrouping: true,
          groupingStartTimestamp,
          error: false,
          finished: false
        }));
      });

      dispatch(getVulnsCountInAllWorkspaces());

      dispatch({ type: types.GENERAL_MANAGE_CREATE_UPDATE_SUCCESS, vulns: res });

      dispatch({ type: RESET_STATE_MANAGE_CREATE_UPDATE });
    } catch (error) {
      return dispatch({ type: MANAGE_CREATE_UPDATE_FAIL, errorMessage: error.message });
    }
  };
}

export function setVulnsGeneralTags (tagsToAdd = [], tagsToRemove = []) {
  return async (dispatch, getState) => {
    try {
      const state = getState();
      const vulnsSelected = selectVulnsSelected(state);
      const vulnsList = selectVulns(state);
      const selectedVulnsIds = vulnsSelected.map(getVulnId).filter((id) => id !== null && typeof id !== 'undefined');
      const selectAll = selectSelectAll(state);

      const advancedFilterQueryParam = selectAdvancedFilterQueryParam(state, 'vulnsGeneral');
      const hasAdvancedFilter = advancedFilterQueryParam.filters.length > 0;
      const standardQueryParam = selectQueryParam('vulnsGeneral', state);
      const queryParam = hasAdvancedFilter ? advancedFilterQueryParam : standardQueryParam;

      if (selectAll) {
        const data = {
          tags_to_add: tagsToAdd,
          tags_to_remove: tagsToRemove
        };
        await api.vulnsGeneral.setAllVulnsTags({ filters: queryParam.filters }, data);
      } else {
        const data = {
          vulnerability_ids: selectedVulnsIds,
          tags_to_add: tagsToAdd,
          tags_to_remove: tagsToRemove
        };

        await api.vulnsGeneral.setVulnsTags(data);
      }

      const newTags = tagsToAdd.filter((tag) => !tagsToRemove.includes(tag));

      selectedVulnsIds.forEach((id) => {
        const vuln = vulnsSelected.find((selectedVuln) => sameVulnId(selectedVuln, { id }));
        if (!vuln) return;
        const newTagsList = newTags.filter((tag) => !vuln.tags.includes(tag));
        vuln.tags = [
          ...vuln.tags.filter((tag) => !tagsToRemove.includes(tag)),
          ...newTagsList
        ];

        const indexVulnsList = vulnsList.findIndex((vulnFromList) => sameVulnId(vulnFromList, vuln));
        if (indexVulnsList > -1 && !isEmpty(vulnsList)) vulnsList.splice(indexVulnsList, 1, vuln);

        const indexVulnsSelected = vulnsSelected.findIndex((selectedVuln) => sameVulnId(selectedVuln, { id }));
        if (indexVulnsList > -1 && !isEmpty(vulnsSelected)) vulnsSelected.splice(indexVulnsSelected, 1, vuln);
      });

      const vulnDetail = selectVulnDetail(state);
      if (!!vulnDetail && selectedVulnsIds.some((id) => sameVulnId(vulnDetail, { id }))) {
        vulnDetail.tags = [
          ...vulnDetail.tags.filter((tag) => !tagsToRemove.includes(tag)),
          ...newTags
        ];
      }

      dispatch({ type: types.GENERAL_MANAGE_SET_TAGS_SUCCESS });

      if (vulnsSelected.length === 1) {
        dispatch({ type: types.GENERAL_MANAGE_TAGS_UPDATE_DETAIL, vuln: vulnDetail });
      }
      dispatch(getGeneralTags());
      dispatch(getGeneralHostsTags());
      dispatch(getGeneralServicesTags());
    } catch (e) {
      dispatch({ type: types.GENERAL_MANAGE_SET_TAGS_FAIL, error: e.message });
    }
  };
}

export function addTag (tag) {
  return (dispatch) => {
    dispatch(setVulnsGeneralTags([tag], []));
  };
}

export function removeTag (tag) {
  return (dispatch) => {
    dispatch(setVulnsGeneralTags([], [tag]));
  };
}

export function refreshVulnEnrichment (vulnBefore) {
  return async (dispatch, getState) => {
    dispatch({ type: types.CONTEXT_REFRESH_VULN_ENRICHMENT_REQUEST });

    const workspace = get(vulnBefore, 'workspace_name', '');
    const id = selectVulnDetailId(getState());

    try {
      const vulnAfter = await api.manage.refreshEnrichment(workspace, id);
      dispatch(vulnPreviewEditSuccedCallback(vulnBefore, vulnAfter));
      dispatch({ type: types.CONTEXT_REFRESH_VULN_ENRICHMENT_SUCCESS });
    } catch (e) {
      dispatch({
        type: types.CONTEXT_REFRESH_VULN_ENRICHMENT_FAILURE,
        errorMessage: 'Faraday was not able to determine the requested Risk Information. Please try again later.'
      });
    }
  };
}

export function updateCvssGeneral (tempCVSS) {
  return (dispatch, getState) => {
    const currentVuln = selectVulnDetail(getState());
    const currentCVSSValue = currentVuln.cvss3.vector_string;

    const parsedTempCVSS = {
      base_score: Number(tempCVSS.score),
      base_severity: tempCVSS.severity.toLowerCase(),
      vector_string: tempCVSS.vector
    };
    dispatch({ type: MANAGE_SET_TEMP_CVSS, tempCVSS: parsedTempCVSS });

    const sameVector = currentCVSSValue === tempCVSS.vector;
    if ((!parsedTempCVSS.vector_string.includes('_')) && (!sameVector)) {
      dispatch(updateVulnGeneral(currentVuln, 'cvss3', { vector_string: tempCVSS.vector }));
    }
  };
}

export function setSelectedComment (selectedComment) {
  return (dispatch) => {
    dispatch({ type: types.GENERAL_MANAGE_SET_SELECTED_COMMENT, selectedComment });
  };
}

function getCommentsSucceedCallback (data) {
  return (dispatch) => {
    dispatch({
      type: types.GENERAL_MANAGE_GET_COMMENTS_SUCCESS,
      data
    });
  };
}

// The plain, no-workspace comment/vulnerability/{id} route (api.vulnsGeneral.getComments)
// doesn't exist on community's backend -- comments are always workspace-scoped there.
// Route through the same ws-scoped endpoint newComment/removeComment/saveComment below
// already use.
export function getComments (ws, id) {
  return async (dispatch) => {
    try {
      dispatch({ type: types.GENERAL_MANAGE_GET_COMMENTS_REQUEST });
      const data = await api.manage.getComments(ws, id);
      dispatch(getCommentsSucceedCallback(data));
    } catch (e) {
      dispatch({ type: types.GENERAL_MANAGE_GET_COMMENTS_FAIL, data: e.message });
    }
  };
}

function addCommentSucceedCallback (data) {
  return (dispatch) => {
    dispatch({
      type: types.GENERAL_MANAGE_ADD_COMMENT_SUCCESS,
      data
    });
  };
}

export function addComment (ws, data) {
  return async (dispatch) => {
    try {
      dispatch({ type: types.GENERAL_MANAGE_ADD_COMMENT_REQUEST });
      const response = await api.manage.newComment(ws, data);
      dispatch(addCommentSucceedCallback(response));
    } catch (e) {
      dispatch({ type: types.GENERAL_MANAGE_ADD_COMMENT_FAIL, data: e.message });
    }
  };
}

function removeCommentSucceedCallback (commentId) {
  return (dispatch) => {
    dispatch({
      type: types.GENERAL_MANAGE_REMOVE_COMMENT_SUCCESS,
      commentId
    });
  };
}

export function removeComment (ws, commentId) {
  return async (dispatch) => {
    try {
      dispatch({ type: types.GENERAL_MANAGE_REMOVE_COMMENT_REQUEST });
      await api.manage.removeComment(ws, commentId);
      dispatch(removeCommentSucceedCallback(commentId));
    } catch (e) {
      dispatch({ type: types.GENERAL_MANAGE_REMOVE_COMMENT_FAIL, data: e.message });
    }
  };
}

function updateCommentSucceedCallback (data) {
  return (dispatch) => {
    dispatch({
      type: types.GENERAL_MANAGE_UPDATE_COMMENT_SUCCESS,
      data
    });
  };
}

export function updateComment (ws, data) {
  return async (dispatch) => {
    try {
      dispatch({ type: types.GENERAL_MANAGE_UPDATE_COMMENT_REQUEST });
      const response = await api.manage.saveComment(ws, data.id, data);
      dispatch(updateCommentSucceedCallback(response));
    } catch (e) {
      dispatch({ type: types.GENERAL_MANAGE_UPDATE_COMMENT_FAIL, data: e.message });
    }
  };
}

export function bulkAddComment () {
  return (dispatch, getState) => {
    const state = getState();
    const vulnsSelected = selectVulnsSelected(state);
    const text = selectModalBulkUpdateValue(state);

    vulnsSelected.forEach((vuln) => {
      const data = {
        text,
        object_type: 'vulnerability',
        object_id: getVulnId(vuln),
        create_date: new Date()
      };
      dispatch(addComment(vuln.workspace_name, data));
    });
  };
}

export const updateCvssV4General = (vector_string) => (dispatch, getState) => {
  const currentVuln = selectVulnDetail(getState());
  dispatch(updateVulnGeneral(currentVuln, 'cvss4', { vector_string }));
};

export const getUsersList = () => async (dispatch, getState) => {
  try {
    const state = getState();
    const vulnsSelected = selectVulnsSelected(state);

    const promises = vulnsSelected.map((vuln) => {
      const response = api.users.getUsersList(vuln.workspace_name);
      return response;
    });

    const data = await Promise.all(promises);
    const uniqueDuplicates = [...new Set(data.flatMap(item => item.usernames))];
    const result = uniqueDuplicates.map(username => ({ name: username }));
    return dispatch({ type: GET_USERS_SUCCESS, users: result });
  } catch (e) {
    return dispatch({ type: '' });
  }
};

export const unLink = (tool, vulnId, issueId) => async (dispatch) => {
  try {
    const data = {
      vuln_id: vulnId,
      issue_id: issueId
    };
    dispatch({ type: types.UNLINK_START });
    const response = await api.settings.unLink(tool.toLowerCase(), data);
    const updatedIssuetracker = get(response, 'updated_issues', {});
    dispatch({ type: types.UNLINK_SUCCESS, payload: { vulnId, updatedIssuetracker } });
  } catch (e) {
    dispatch({ type: types.UNLINK_FAILED, data: e.message });
  }
};

export const toApiUtcDate = (date) => {
  return date.toISOString().split('.')[0];
};
